import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ContactForm.css';
import logoWhite from '../../Assets/JMD INFORMATIQUE WHITE.png';

function ContactForm() {
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', telephone: '', message: '', _trap: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = 'Contact — JMD Informatique · Six-Fours & Var';
    document.querySelector('meta[name="description"]')
      ?.setAttribute('content', 'Contactez JMD Informatique pour un dépannage informatique à domicile à Six-Fours ou environs. Réponse rapide garantie. ☎ 07 81 16 53 06');
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot — bots fill hidden fields, humans don't
    if (form._trap) return;

    const nom = form.nom.trim();
    const prenom = form.prenom.trim();
    const email = form.email.trim();
    const telephone = form.telephone.trim();
    const message = form.message.trim();

    if (!nom) { toast.error('Veuillez indiquer votre nom.'); return; }
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) { toast.error('Adresse email invalide.'); return; }
    if (telephone && !telephone.match(/^[\d\s().+-]{7,20}$/)) { toast.error('Numéro de téléphone invalide.'); return; }
    if (!message) { toast.error('Veuillez écrire un message.'); return; }

    setSending(true);

    const emailData = {
      subject: `Nouveau message JMD Informatique — ${nom} ${prenom}`,
      message: `Nom: ${nom}\nPrénom: ${prenom}\nEmail: ${email}\nTéléphone: ${telephone}\nMessage: ${message}`,
      send_to: 'contact@jmdinformatique.com',
      sent_from: email,
      reply_to: email,
    };

    try {
      const response = await fetch('https://mail.jmdwebdev.com/', {
        mode: 'cors',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData),
      });

      if (response.ok) {
        toast.success('Message envoyé ! Je vous recontacte rapidement.');
        setForm({ nom: '', prenom: '', email: '', telephone: '', message: '', _trap: '' });
      } else {
        throw new Error('Erreur serveur');
      }
    } catch {
      toast.error('Envoi impossible, veuillez réessayer ou appeler directement.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact" id="main-content">
      <div className="contact__inner">
        {/* Header */}
        <div className="contact__header">
          <img src={logoWhite} alt="JMD Informatique" className="contact__logo-img" />
        </div>

        <h1 className="contact__title">Me contacter</h1>
        <p className="contact__intro">
          Décrivez votre problème informatique ou votre demande — je vous réponds dans les plus brefs délais.
          Vous pouvez aussi <a href="tel:0781165306">appeler directement</a>.
        </p>

        {/* Phone CTA */}
        <a href="tel:0781165306" className="contact__phone-cta">
          <i className="fa-solid fa-phone"></i>
          07 81 16 53 06
        </a>

        <div className="contact__separator">
          <span>ou envoyez un message</span>
        </div>

        {/* Form */}
        <form className="contact__form" onSubmit={handleSubmit} noValidate>
          {/* Honeypot — hidden from users, filled by bots */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
            <input
              type="text"
              name="_trap"
              value={form._trap}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>
          <div className="form__row">
            <div className="form__group">
              <label htmlFor="nom" className="form__label">Nom *</label>
              <input
                type="text"
                id="nom"
                name="nom"
                className="form__input"
                placeholder="Dupont"
                value={form.nom}
                onChange={handleChange}
                maxLength={80}
                required
              />
            </div>
            <div className="form__group">
              <label htmlFor="prenom" className="form__label">Prénom</label>
              <input
                type="text"
                id="prenom"
                name="prenom"
                className="form__input"
                placeholder="Marie"
                value={form.prenom}
                onChange={handleChange}
                maxLength={80}
              />
            </div>
          </div>

          <div className="form__row">
            <div className="form__group">
              <label htmlFor="email" className="form__label">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form__input"
                placeholder="marie.dupont@email.com"
                value={form.email}
                onChange={handleChange}
                maxLength={150}
                required
              />
            </div>
            <div className="form__group">
              <label htmlFor="telephone" className="form__label">Téléphone</label>
              <input
                type="tel"
                id="telephone"
                name="telephone"
                className="form__input"
                placeholder="06 00 00 00 00"
                value={form.telephone}
                onChange={handleChange}
                maxLength={20}
              />
            </div>
          </div>

          <div className="form__group form__group--full">
            <label htmlFor="message" className="form__label">Décrivez votre problème *</label>
            <textarea
              id="message"
              name="message"
              className="form__textarea"
              rows="7"
              placeholder="Mon ordinateur est très lent depuis quelques jours, j'ai aussi des problèmes de connexion wifi…"
              value={form.message}
              onChange={handleChange}
              maxLength={2000}
              required
            ></textarea>
          </div>

          <button type="submit" className="form__submit" disabled={sending}>
            {sending ? (
              <><i className="fa-solid fa-circle-notch fa-spin"></i> Envoi…</>
            ) : (
              <><i className="fa-solid fa-paper-plane"></i> Envoyer le message</>
            )}
          </button>
        </form>

        {/* Info */}
        <div className="contact__info-strip">
          <div className="contact__info-item">
            <i className="fa-solid fa-location-dot"></i>
            <span>Six-Fours & environs</span>
          </div>
          <div className="contact__info-item">
            <i className="fa-solid fa-clock"></i>
            <span>Soirs dès 20h · Week-ends</span>
          </div>
          <div className="contact__info-item">
            <i className="fa-solid fa-car"></i>
            <span>Déplacements inclus</span>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        theme="dark"
      />
    </div>
  );
}

export default ContactForm;
